import type { Execute, GetQuoteParameters } from '@reservoir0x/relay-sdk'
import { type Address, encodeFunctionData } from 'viem'
import { getRelayClient } from './client'
import {
  FUN_RELAY_REFERRER,
  FUN_RELAY_REVENUE_WALLET,
  RELAY_QUOTE_CHECKOUT_TIME_BUFFER_MS,
} from './constants'
import { getRelayAssetInfo } from './currency'
import { getFunRelayFees, parseRelayFees } from './fees'
import { getRelayAssetPriceInfo } from './price'
import type {
  ApiFunkitCheckoutActionParams,
  CheckoutQuoteResponse,
  Logger,
} from './types'
import { convertFunToRelayTokenAddress } from './utils'

type RelayQuoteOptions = GetQuoteParameters['options'] & {}

export type GetRelayQuoteParams = {
  actionParams?: ApiFunkitCheckoutActionParams[]
  fromChainId: string
  fromTokenAddress: Address
  logger: Logger
  /**
   * {@link getRelayQuote} already sets {@link RelayQuoteOptions.appFees|appFees} and {@link RelayQuoteOptions.referrer|referrer}.
   * Only include them if you wish to override.
   */
  options?: RelayQuoteOptions
  recipientAddress: Address | undefined
  toChainId: string
  toTokenAddress: Address
  tradeType: 'EXACT_INPUT' | 'EXACT_OUTPUT'
  userAddress: Address | undefined
  clientId: string
} & (
  | {
      fromTokenAmountBaseUnit: bigint | string
      tradeType: 'EXACT_INPUT'
    }
  | {
      toTokenAmountBaseUnit: bigint | string
      tradeType: 'EXACT_OUTPUT'
    }
)

export type RelayQuote = CheckoutQuoteResponse & {
  /** Required for EXACT_INPUT checkouts */
  finalToAmountBaseUnit: string
  metadata: {
    fromAmountBaseUnit: string
    relayQuote: Execute
    toAmountBaseUnit: string
  }
}

async function getQuoteEstUsdValue({
  tokenChainId,
  tokenAddress,
  tokenAmountBaseUnit,
  logger,
}: {
  tokenChainId: string
  tokenAddress: string
  tokenAmountBaseUnit: string
  logger: Logger
}) {
  try {
    const [tokenPriceRes, tokenInfoRes] = await Promise.all([
      getRelayAssetPriceInfo({
        chainId: tokenChainId,
        address: convertFunToRelayTokenAddress(tokenAddress as Address),
      }),
      getRelayAssetInfo({
        chainId: tokenChainId,
        address: convertFunToRelayTokenAddress(tokenAddress as Address),
      }),
    ])

    const price = tokenPriceRes.price
    const decimals = tokenInfoRes.decimals

    const absoluteAmount = BigInt(tokenAmountBaseUnit) / BigInt(10 ** decimals)
    return Number(price) * Number(absoluteAmount)
  } catch (err) {
    logger.error('getQuoteEstUsdValueError', {
      message: `Failed to get USD value for token ${tokenAddress} on chain ${tokenChainId}: ${
        err instanceof Error ? err.message : JSON.stringify(err)
      }. Falling back to 0 USD.`,
    })
    // If any part fails, just fallback to 0 USD so that we do not block quote
    return 0
  }
}

export async function getRelayQuote({
  logger,
  ...params
}: GetRelayQuoteParams): Promise<RelayQuote> {
  const {
    actionParams,
    fromChainId,
    fromTokenAddress,
    options,
    recipientAddress,
    toChainId,
    toTokenAddress,
    userAddress,
    clientId,
  } = params

  try {
    const relayClient = getRelayClient()

    const txs = actionParams?.map((action) => {
      const data = encodeFunctionData({
        abi: action.contractAbi,
        args: action.functionArgs,
        functionName: action.functionName,
      })

      return {
        data,
        to: action.contractAddress,
        value: String(action.value ?? 0n),
      }
    })

    const isExactIn = params.tradeType === 'EXACT_INPUT'
    const queryTokenAmountBaseUnit = isExactIn
      ? params.fromTokenAmountBaseUnit
      : params.toTokenAmountBaseUnit
    const queryTokenAddress = isExactIn ? fromTokenAddress : toTokenAddress
    const queryTokenChainId = isExactIn ? fromChainId : toChainId

    const estUsdValue = await getQuoteEstUsdValue({
      tokenChainId: queryTokenChainId,
      tokenAddress: queryTokenAddress,
      tokenAmountBaseUnit: queryTokenAmountBaseUnit.toString(),
      logger,
    })

    const feeInfo = await getFunRelayFees({
      fromTokenAddress,
      fromChainId,
      toTokenAddress,
      toChainId,
      estUsdValue: Number(estUsdValue) || 0,
      clientId,
    })

    const appFeeBp = feeInfo.b + feeInfo.v

    logger.info('getRelayQuoteParams', {
      ...params,
      queryTokenAmountBaseUnit,
      txs,
      estUsdValue,
      feeInfo,
      appFeeBp,
    })

    // Get the full quote with fees
    const relayQuote = await relayClient.actions.getQuote({
      chainId: Number(fromChainId),
      toChainId: Number(toChainId),
      currency: convertFunToRelayTokenAddress(fromTokenAddress),
      toCurrency: convertFunToRelayTokenAddress(toTokenAddress),
      amount: queryTokenAmountBaseUnit.toString(),
      recipient: recipientAddress,
      tradeType: params.tradeType,
      user: userAddress,
      ...(txs && { txs }),
      options: {
        referrer: clientId
          ? `${FUN_RELAY_REFERRER}|${clientId}`
          : FUN_RELAY_REFERRER,
        appFees: [
          {
            fee: appFeeBp.toString(),
            recipient: FUN_RELAY_REVENUE_WALLET,
          },
        ],
        ...options,
      },
    })

    logger.info('relayQuote', relayQuote)

    const estSubtotalUsd = Number(
      relayQuote.details?.currencyOut?.amountUsd || 0,
    )
    const estTotalUsd = Number(relayQuote.details?.currencyIn?.amountUsd || 0)
    // Get how much fromAmount is needed for the quote
    const fromAmountString =
      relayQuote.details?.currencyIn?.amountFormatted || '0'
    const fromAmountBaseUnitString =
      relayQuote.details?.currencyIn?.amount || '0'

    // RequestID will be consistent across steps in the quote
    // https://fun-xyz.slack.com/archives/C08MQ85QB2N/p1747774944603209
    const relayRequestId = relayQuote.steps[0]?.requestId

    // TODO: Verify with relay team if this is possible?
    if (!relayRequestId) {
      throw new Error('Relay quote does not contain a requestId')
    }

    // TODO: Verify with relay team if this is possible?
    if (!relayQuote.details?.currencyIn || !relayQuote.details?.currencyOut) {
      throw new Error('Relay quote does not contain trade details')
    }

    const {
      relayGasFeesUsd,
      totalLpFeesUsd,
      totalFeesUsd,
      totalFeesFromAmount,
      totalFeesFromAmountBaseUnit,
    } = parseRelayFees({ fees: relayQuote.fees, logger })

    const estSubtotalFromAmount = (
      Number.parseFloat(fromAmountString) - totalFeesFromAmount
    ).toString()

    const estSubtotalFromAmountBaseUnit = (
      BigInt(fromAmountBaseUnitString) - totalFeesFromAmountBaseUnit
    ).toString()

    return {
      quoteId: relayRequestId,
      estCheckoutTimeMs:
        (relayQuote.details.timeEstimate || 0) * 1000 +
        RELAY_QUOTE_CHECKOUT_TIME_BUFFER_MS,
      estFeesFromAmount: totalFeesFromAmount.toString(),
      estFeesFromAmountBaseUnit: totalFeesFromAmountBaseUnit.toString(),
      estFeesUsd: totalFeesUsd,
      estMarketMakerGasUsd: relayGasFeesUsd,
      estSubtotalFromAmount,
      estSubtotalFromAmountBaseUnit,
      estSubtotalUsd,
      estTotalFromAmount: fromAmountString,
      estTotalFromAmountBaseUnit: fromAmountBaseUnitString,
      estTotalUsd,
      finalToAmountBaseUnit: relayQuote.details.currencyOut.amount ?? '0', // TODO: Or do we want 'minimumAmount'?
      fromTokenAddress,
      lpFeePercentage: 0, // TODO: ?
      lpFeeUsd: totalLpFeesUsd,
      metadata: {
        fromAmountBaseUnit: isExactIn
          ? queryTokenAmountBaseUnit.toString()
          : (relayQuote.details.currencyIn.amount ?? '0'),
        relayQuote,
        toAmountBaseUnit: isExactIn
          ? (relayQuote.details.currencyOut.amount ?? '0')
          : queryTokenAmountBaseUnit.toString(),
      },
    }
  } catch (err) {
    throw new Error(
      `An error occurred trying to generate a relay quote: ${
        err instanceof Error ? err.message : JSON.stringify(err)
      }`,
    )
  }
}
